'use client'

import { useEffect, useState } from 'react'
import type { Locale } from '@/lib/content'

const STORAGE_KEY = 'wf_consent'

type ConsentCopy = {
  text: string
  accept: string
  decline: string
  privacy: string
  privacySlug: string
  cookie: string
  cookieSlug: string
}

const COPY: Record<Locale, ConsentCopy> = {
  nl: {
    text: 'We gebruiken cookies om onze website te verbeteren en advertenties te meten.',
    accept: 'Accepteren',
    decline: 'Weigeren',
    privacy: 'Privacybeleid',
    privacySlug: 'privacy',
    cookie: 'Cookieverklaring',
    cookieSlug: 'cookiebeleid',
  },
  en: {
    text: 'We use cookies to improve our website and measure advertising.',
    accept: 'Accept',
    decline: 'Decline',
    privacy: 'Privacy policy',
    privacySlug: 'privacy',
    cookie: 'Cookie policy',
    cookieSlug: 'cookie-policy',
  },
  de: {
    text: 'Wir verwenden Cookies, um unsere Website zu verbessern und Werbung zu messen.',
    accept: 'Akzeptieren',
    decline: 'Ablehnen',
    privacy: 'Datenschutz',
    privacySlug: 'datenschutz',
    cookie: 'Cookie-Richtlinie',
    cookieSlug: 'cookie-richtlinie',
  },
  fr: {
    text: 'Nous utilisons des cookies pour améliorer notre site et mesurer la publicité.',
    accept: 'Accepter',
    decline: 'Refuser',
    privacy: 'Confidentialité',
    privacySlug: 'confidentialite',
    cookie: 'Politique en matière de cookies',
    cookieSlug: 'politique-cookies',
  },
}

export function CookieConsent({ locale }: { locale: Locale }): React.JSX.Element | null {
  const [visible, setVisible] = useState(false)
  const copy = COPY[locale] ?? COPY.nl

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        setVisible(true)
      }
    } catch {
      setVisible(true)
    }
  }, [])

  const decide = (granted: boolean): void => {
    try {
      localStorage.setItem(STORAGE_KEY, granted ? 'granted' : 'denied')
    } catch {
      /* storage unavailable - still hide the banner for this session */
    }
    if (granted && typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        analytics_storage: 'granted',
      })
    }
    setVisible(false)
  }

  if (!visible) {
    return null
  }

  return (
    <div
      role="dialog"
      aria-label={copy.privacy}
      className="fixed bottom-4 left-4 right-4 z-[100] mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-4 shadow-xl sm:right-auto"
    >
      <p className="text-sm text-slate-700">
        {copy.text}{' '}
        <a
          href={`/${locale}/${copy.privacySlug}/`}
          className="font-medium text-brand-600 underline hover:text-brand-700"
        >
          {copy.privacy}
        </a>
        {' · '}
        <a
          href={`/${locale}/${copy.cookieSlug}/`}
          className="font-medium text-brand-600 underline hover:text-brand-700"
        >
          {copy.cookie}
        </a>
      </p>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => decide(true)}
          className="flex-1 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-700"
        >
          {copy.accept}
        </button>
        <button
          type="button"
          onClick={() => decide(false)}
          className="flex-1 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
        >
          {copy.decline}
        </button>
      </div>
    </div>
  )
}
